// R115 calendar hub — OPS rail providers for the "📥 To do" worklist: what has
// no day yet or needs a person (unassigned cleans / inspections, tasks with no
// home, at-risk arrivals, unhappy guests in house, open recoveries).
import { registerRail, type RailItem, type CalendarCtx } from '../registry';
import type { Stay } from '../types';
import { unassignedCleans, inspUnassigned, isAtRiskArrival, isNegativeInHouse, isRecoveryOpen, inspKindLabel } from '../../../../../lib/calendarOps.mjs';
import { insp, tasks, health, propName, viewIds } from './util';

registerRail({
  key: 'unassigned_cleans', label: 'Cleans to assign',
  items: (ctx) => unassignedCleans((ctx.data.events && ctx.data.events.turns) || [], ctx.asOf)
    .filter(t => !!ctx.property(t.listing_id))
    .map((t): RailItem => ({
      key: `turn:${t.id}`, entity: { kind: 'turn', id: t.id, listing_id: t.listing_id, date: t.date }, listing_id: t.listing_id, date: t.date,
      title: propName(ctx, t.listing_id), sub: `Clean · ${ctx.fmt.wmd(t.date)}${t.same_day ? ' · same-day' : ''}${t.at_risk ? ' · at risk' : ''}`,
      tone: t.at_risk ? 'bad' : 'warn', search: `${propName(ctx, t.listing_id)} clean`,
    })),
});
registerRail({
  key: 'unassigned_inspections', label: 'Inspections to assign', layer: 'inspections',
  items: (ctx) => {
    const L = insp(ctx);
    if (!L) return [];
    const ids = new Set(viewIds(ctx));
    return inspUnassigned(L.jobs, ctx.asOf).filter(j => ids.has(j.listing_id)).map((j): RailItem => {
      const day = j.roll_to || j.date;
      return {
        key: `insp:${j.id}`, entity: { kind: 'inspection', id: j.id, listing_id: j.listing_id, date: j.date }, listing_id: j.listing_id, date: day,
        title: propName(ctx, j.listing_id), sub: `${j.missed ? '⏰ Missed · ' : ''}Inspection · ${inspKindLabel(j)} · ${ctx.fmt.wmd(day)}`,
        tone: j.missed ? 'bad' : 'warn', search: `${propName(ctx, j.listing_id)} inspection`,
      };
    });
  },
});
registerRail({
  key: 'tasks_no_listing', label: 'Office tasks', layer: 'tasks',
  items: (ctx) => {
    const L = tasks(ctx);
    if (!L) return [];
    return L.items.filter(t => !t.listing_id).map((t): RailItem => ({
      key: `task:${t.id}`, entity: { kind: 'task', id: t.id, date: t.due_date }, listing_id: null, date: t.due_date,
      title: t.title, sub: `${t.overdue ? 'Overdue · ' : ''}due ${ctx.fmt.md(t.due_date)}${t.assignees.length ? ` · ${t.assignees.map(a => a.name).join(', ')}` : ''}`,
      tone: t.overdue ? 'bad' : t.priority === 'urgent' || t.priority === 'high' ? 'warn' : 'info', search: t.title,
    }));
  },
});
const stayItem = (ctx: CalendarCtx, s: Stay, sub: string, tone: RailItem['tone']): RailItem => ({
  key: `stay:${s.id}`, entity: { kind: 'stay', id: s.id, listing_id: s.listing_id, date: s.check_in }, listing_id: s.listing_id, date: s.check_in,
  title: propName(ctx, s.listing_id), sub, tone, search: `${propName(ctx, s.listing_id)} ${s.guest_name || ''}`,
});
const ROLLUP: Record<string, string> = { at_risk: 'checklist at risk', blocked: 'checklist blocked' };
registerRail({
  key: 'at_risk_arrivals', label: 'At-risk arrivals',
  items: (ctx) => ctx.data.stays.filter(s => isAtRiskArrival(s, ctx.asOf))
    .sort((a, b) => (a.check_in < b.check_in ? -1 : 1))
    .map(s => stayItem(ctx, s, `Arrives ${ctx.fmt.wmd(s.check_in)} · ${ROLLUP[(s.checks && s.checks.rollup) || ''] || 'at risk'}${s.checks && s.checks.required_total ? ` · ${s.checks.required_done}/${s.checks.required_total}` : ''}`, 'bad')),
});
registerRail({
  key: 'negative_in_house', label: 'Unhappy guests in house',
  items: (ctx) => ctx.data.stays.filter(isNegativeInHouse)
    .map(s => stayItem(ctx, s, `In house until ${ctx.fmt.wmd(s.check_out)}${s.flagged && s.flagged.category ? ` · ${s.flagged.category}` : ''}`, 'bad')),
});
registerRail({
  key: 'recovery_open', label: 'Recovery open',
  items: (ctx) => ctx.data.stays.filter(isRecoveryOpen)
    .map(s => stayItem(ctx, s, `${s.state === 'in_house' ? 'In house' : s.state === 'checked_out' ? `Left ${ctx.fmt.md(s.check_out)}` : `Arrives ${ctx.fmt.md(s.check_in)}`} · recovery open`, 'bad')),
});
// R115 review (A5 "open supply requests"): homes with open supply requests, from the health layer the Ops view already
// loads — each row opens the home's panel (its Home health section lists them, with the Supplies button).
registerRail({
  key: 'supply_requests', label: 'Supply requests', layer: 'health',
  items: (ctx) => {
    const L = health(ctx);
    if (!L) return [];
    const ids = new Set(viewIds(ctx));
    return Object.entries(L.by_listing || {})
      .filter(([lid, r]) => ids.has(lid) && !!r && (r.supply_open || 0) > 0)
      .sort((a, b) => (b[1].supply_open || 0) - (a[1].supply_open || 0))
      .map(([lid, r]): RailItem => ({
        key: `supply:${lid}`, entity: { kind: 'listing', id: lid, listing_id: lid }, listing_id: lid, date: null,
        title: propName(ctx, lid), sub: `📦 ${r.supply_open} open supply request${r.supply_open === 1 ? '' : 's'}`,
        tone: 'warn', search: `${propName(ctx, lid)} supply supplies request`,
      }));
  },
});
